/**
 * OnlineJournal.tsx
 * Главный компонент журнала сроковых препаратов (admin).
 *
 * Роль: ТОЛЬКО оркестрация.
 * - Соединяет хуки (данные) с компонентами (отображение)
 * - Управляет side effects (useEffect для lazy load аналитики, обновления при смене фильтров)
 * - Сам не содержит бизнес-логики или прямых API-вызовов
 *
 * Версия: v5 (рефакторинг AdminJournal v4 → OnlineJournal)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Tabs, Tab } from '@mui/material';
import {
  TableChart as TableIcon,
  Analytics as AnalyticsIcon,
} from '@mui/icons-material';
import axios from 'axios';

// ── Layers ────────────────────────────────────────────────────────────────────
import {
  useJournalFilters,
  useJournalEntries,
  useJournalKpi,
  useFilterOptions,
  useAnalytics,
  useUsersActivity,
  useColumnVisibility,
} from './hooks';
import { fmtSum, getMonthsList } from './utils';
import { API_BASE } from './constants';

// ── Components ────────────────────────────────────────────────────────────────
import KpiBar from './components/KpiBar';
import JournalToolbar from './components/JournalToolbar';
import JournalTable from './components/JournalTable';
import AnalyticsPanel from './components/AnalyticsPanel';
import ColumnVisibilityMenu from './components/ColumnVisibilityMenu';

// ─── Main Component ───────────────────────────────────────────────────────────
const OnlineJournal: React.FC = () => {
  // ── Filters, sort, pagination ──
  const filters = useJournalFilters();
  const monthsList = useMemo(() => getMonthsList(), []);

  // ── Table data ──
  const { entries, tableLoading, pagination, fetchEntries } = useJournalEntries(
    filters.filterParams,
    filters.orderBy,
    filters.order,
  );

  // ── KPI ──
  const { kpi, kpiLoading, fetchKpi } = useJournalKpi(filters.filterParams);

  // ── Filter options ──
  const { filterOptions, fetchFilterOptions } = useFilterOptions(
    filters.selectedMonth,
    filters.selectedPharmacy,
    filters.selectedProduct,
  );


  // ── Analytics ──
  const {
    monthlyData, expiryData, pharmacyDistData, productDynData, movementsData,
    analyticsLoading, fetchAnalytics, fetchProductDynamics,
  } = useAnalytics();

  // ── Users activity ──
  const {
    usersActivity, usersActivityLoading,
    activitySearch, setActivitySearch,
    activityLoaded, activityMonth,
    fetchUsersActivity,
  } = useUsersActivity();

  // ── Producer map (номенклатура → производитель) ──
  const [producerMap, setProducerMap] = useState<Map<string, string>>(new Map());
  const [producerMapLoaded, setProducerMapLoaded] = useState(false);

  // ── Column visibility ──
  const { visibleColumns, visibleDefs, gridTemplate, toggle, showAll, reset: resetColumns } = useColumnVisibility();

  // ── UI tabs ──
  const [contentTab, setContentTab] = useState(0);
  const [chartTab, setChartTab] = useState(0);

  // ── Chart 3 local filters ──
  const [c3Pharmacy, setC3Pharmacy] = useState('all');
  const [c3Product, setC3Product] = useState<string | null>(null);

  // ─── Effects ──────────────────────────────────────────────────────────────

  // Загрузка карты производителей (однократно)
  useEffect(() => {

    document.title = 'Электронный журнал | Zerde';

    if (producerMapLoaded) return;
    axios.get(`${API_BASE}/nomenclature`)
      .then(r => {
        if (r.data?.success) {
          const map = new Map<string, string>();
          (r.data.data as any[]).forEach(n => {
            if (n.productSku && n.producer) map.set(String(n.productSku), String(n.producer));
          });
          setProducerMap(map);
          setProducerMapLoaded(true);
        }
      })
      .catch(() => { });
  }, [producerMapLoaded]);

  // При смене фильтров — сбрасываем на первую страницу
  useEffect(() => {
    filters.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.filterParams]);

  // Таблица + KPI — перезагружаются при смене страницы или фильтров
  useEffect(() => {
    fetchEntries(filters.page);
    fetchKpi();
  }, [filters.page, fetchEntries, fetchKpi]);

  // Фильтры-дропдауны — при смене месяца или аптеки
  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  // Аналитика — при первом открытии вкладки (lazy)
  useEffect(() => {
    if (contentTab === 1) fetchAnalytics();
  }, [contentTab, fetchAnalytics]);

  // График 3 — при переходе на вкладку
  useEffect(() => {
    if (contentTab === 1 && chartTab === 2) {
      fetchProductDynamics({
        pharmacyNum: c3Pharmacy !== 'all' ? c3Pharmacy : undefined,
        materialName: c3Product ?? undefined,
      });
    }
  }, [contentTab, chartTab, c3Pharmacy, c3Product, fetchProductDynamics]);

  // Активность пользователей — при переходе или смене месяца
  useEffect(() => {
    if (contentTab === 1 && chartTab === 3) {
      if (!activityLoaded || activityMonth !== filters.selectedMonth) {
        fetchUsersActivity(filters.selectedMonth);
      }
    }
  }, [contentTab, chartTab, filters.selectedMonth, activityLoaded, activityMonth, fetchUsersActivity]);

  // ─── Computed ──────────────────────────────────────────────────────────────

  // Обогащаем записи данными о производителе из карты номенклатуры
  const enrichedEntries = useMemo(
    () => entries.map(e => ({
      ...e,
      producer: e.producer || producerMap.get(e.nomenclatureCode) || '',
    })),
    [entries, producerMap]
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* ── Toolbar ── */}
      <JournalToolbar
        selectedMonth={filters.selectedMonth}
        setSelectedMonth={filters.setSelectedMonth}
        selectedPharmacy={filters.selectedPharmacy}
        setSelectedPharmacy={filters.setSelectedPharmacy}
        selectedProduct={filters.selectedProduct}
        setSelectedProduct={filters.setSelectedProduct}
        selectedSerial={filters.selectedSerial}
        setSelectedSerial={filters.setSelectedSerial}
        selectedCategory={filters.selectedCategory}
        setSelectedCategory={filters.setSelectedCategory}
        selectedManager={filters.selectedManager}
        setSelectedManager={filters.setSelectedManager}
        selectedProducer={filters.selectedProducer}
        setSelectedProducer={filters.setSelectedProducer}
        selectedPriceGroup={filters.selectedPriceGroup}
        setSelectedPriceGroup={filters.setSelectedPriceGroup}
        expiryFrom={filters.expiryFrom}
        setExpiryFrom={filters.setExpiryFrom}
        expiryTo={filters.expiryTo}
        setExpiryTo={filters.setExpiryTo}
        filterOptions={filterOptions}
        monthsList={monthsList}
        filterParams={filters.filterParams}
        orderBy={filters.orderBy}
        order={filters.order}
        kpi={kpi}
        onReset={filters.handleReset}
        onRefresh={() => { fetchEntries(filters.page); fetchKpi(); }}
        visibleColumns={visibleColumns}
      />

      {/* ── Навигационная строка: таб-меню (50%) | KPI-бар (50%) ── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '0.5fr 1.5fr',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        mt: -0.5,
        gap: 2,
      }}>
        {/* Левая половина: табы + кнопка управления колонками */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tabs
            value={contentTab}
            onChange={(_, v) => setContentTab(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{
              flex: 1,
              '& .MuiTab-root': {
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '0.9rem',
                minHeight: 44,
              },
            }}
          >
            <Tab icon={<TableIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Таблица" />
            <Tab icon={<AnalyticsIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Аналитика" />
          </Tabs>
          <ColumnVisibilityMenu
            visibleColumns={visibleColumns}
            toggle={toggle}
            showAll={showAll}
            reset={resetColumns}
          />
        </Box>

        {/* Правая половина: KPI-бар на всю ширину */}
        <Box sx={{ mb: -0.5 }}>
          <KpiBar kpi={kpi} kpiLoading={kpiLoading} />
        </Box>
      </Box>

      {/* ── Table Tab ── */}
      {contentTab === 0 && (
        <JournalTable
          entries={enrichedEntries}
          tableLoading={tableLoading}
          pagination={pagination}
          kpi={kpi}
          page={filters.page}
          orderBy={filters.orderBy}
          order={filters.order}
          onSort={filters.handleSort}
          onPageChange={filters.setPage}
          filterParams={filters.filterParams}
          selectedMonth={filters.selectedMonth}
          monthsList={monthsList}
          pharmacies={filterOptions.pharmacies}
          visibleDefs={visibleDefs}
          gridTemplate={gridTemplate}
        />
      )}

      {/* ── Analytics Tab ── */}
      {contentTab === 1 && (
        <AnalyticsPanel
          monthlyData={monthlyData}
          expiryData={expiryData}
          pharmacyDistData={pharmacyDistData}
          productDynData={productDynData}
          movementsData={movementsData}
          usersActivity={usersActivity}
          analyticsLoading={analyticsLoading}
          usersActivityLoading={usersActivityLoading}
          filterOptions={filterOptions}
          selectedMonth={filters.selectedMonth}
          monthsList={monthsList}
          activitySearch={activitySearch}
          setActivitySearch={setActivitySearch}
          c3Pharmacy={c3Pharmacy}
          setC3Pharmacy={setC3Pharmacy}
          c3Product={c3Product}
          setC3Product={setC3Product}
          onFetchProductDynamics={fetchProductDynamics}
          onFetchUsersActivity={fetchUsersActivity}
          chartTab={chartTab}
          setChartTab={setChartTab}
        />
      )}
    </Box>
  );
};

export default OnlineJournal;
